/**
 * YLI Fabric Cart JavaScript v1.4.0
 * ==================================
 * Handles fabric-specific cart functionality:
 * 1. Post-add consolidation: merge duplicate fabric line items after every add-to-cart
 * 2. Auto-update cart when fabric quantity dropdown changes (with Yardage sync)
 * 3. Calculate and add yardage property to line items
 *
 * Architecture:
 * - Uses event delegation for efficient handling
 * - Targets elements with [data-fabric-quantity-select] attribute
 * - Works with both cart page and cart drawer
 * - Uses direct /cart/change.js fetch for quantity + Yardage property updates
 *
 * v1.4.0 - Fix: use product_type as primary fabric guard (handles cached-page adds);
 *           fix: update stale Yardage on auto-merged single items;
 *           add: per-variant debounce (500ms) for rapid clicks
 * v1.3.0 - Replace pre-add intercept (defeated by button-level listeners) with
 *           post-add consolidation via theme:product:add event
 * v1.2.0 - Fix duplicate fabric line items by merging on add-to-cart; fix stale
 *           Yardage property by updating it on cart quantity changes
 * v1.1.0 - Fixed cart page quantity update by using theme's event system
 */

(function() {
  'use strict';

  /**
   * Initialize fabric cart functionality
   * Called on DOMContentLoaded
   */
  function initFabricCart() {
    // Consolidate duplicate fabric line items after every add-to-cart
    initFabricConsolidation();
    // Auto-update cart when fabric quantity dropdown changes
    initFabricQuantitySelectors();
  }

  /**
   * Initialize fabric quantity selector auto-submit
   * Uses event delegation on document for dynamic content
   */
  function initFabricQuantitySelectors() {
    document.addEventListener('change', function(event) {
      const target = event.target;

      // Check if the changed element is a fabric quantity selector
      if (target.hasAttribute('data-fabric-quantity-select')) {
        handleFabricQuantityChange(target);
      }
    });
  }

  /**
   * Handle fabric quantity change
   * Updates both quantity and Yardage property in a single /cart/change.js call.
   * The theme's event-based approach omits properties, causing stale Yardage values.
   * @param {HTMLSelectElement} selector - The fabric quantity select element
   */
  function handleFabricQuantityChange(selector) {
    const lineItemKey = selector.getAttribute('data-id');
    const newQuantity = parseInt(selector.value, 10);

    if (!lineItemKey || isNaN(newQuantity)) {
      return;
    }

    const newYardage = calculateYardage(newQuantity, 0.25);

    // Show loading state
    selector.disabled = true;
    selector.style.opacity = '0.6';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: newQuantity,
        properties: { Yardage: newYardage }
      })
    })
      .then(function(res) { return res.json(); })
      .then(function() {
        // Trigger Broadcast theme's cart rebuild (theme.dev.js:1306 listens for this)
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      })
      .catch(function() {
        selector.disabled = false;
        selector.style.opacity = '1';
      });
  }

  /**
   * Calculate yardage from quantity
   * @param {number} quantity - The unit quantity
   * @param {number} increment - The yard increment (default 0.25)
   * @returns {string} Formatted yardage string (e.g., "2 yards")
   */
  function calculateYardage(quantity, increment) {
    increment = increment || 0.25;
    const yards = quantity * increment;

    // Format: remove trailing zeros, add "yard" or "yards"
    const formattedYards = yards % 1 === 0 ? yards.toString() : yards.toFixed(2);
    const unit = yards === 1 ? 'yard' : 'yards';

    return formattedYards + ' ' + unit;
  }

  /**
   * Add yardage property to fabric product form before submission
   * This ensures the yardage appears in checkout, orders, and emails
   * @param {HTMLFormElement} form - The product form
   * @param {number} quantity - The selected quantity
   */
  function addYardageProperty(form, quantity) {
    const increment = 0.25;
    const yardage = calculateYardage(quantity, increment);

    // Check if yardage input already exists
    let yardageInput = form.querySelector('input[name="properties[Yardage]"]');

    if (!yardageInput) {
      // Create hidden input for yardage property
      yardageInput = document.createElement('input');
      yardageInput.type = 'hidden';
      yardageInput.name = 'properties[Yardage]';
      form.appendChild(yardageInput);
    }

    yardageInput.value = yardage;
  }

  /**
   * Post-add consolidation: after every successful add-to-cart, scan the cart
   * for duplicate fabric line items (same variant_id) and merge them into a
   * single line item with the correct total Yardage.
   *
   * Uses product_type from /cart/add.js response as primary fabric guard,
   * with Yardage property as fallback. This handles cached-page adds where
   * the Yardage hidden input may not be present in the form.
   *
   * Per-variant debounce (500ms) prevents race conditions on rapid clicks.
   */
  function initFabricConsolidation() {
    var pendingVariants = {};

    document.addEventListener('theme:product:add', function(event) {
      var addedItem = event.detail && event.detail.response;
      if (!addedItem) return;

      // Primary: product_type from /cart/add.js response (works even without Yardage input)
      // Fallback: Yardage property exists (non-cached pages)
      var isFabric = addedItem.product_type === 'Fabric';
      if (!isFabric) {
        var addedProperties = addedItem.properties || {};
        if (addedProperties['Yardage'] === undefined) return;
      }

      var variantId = addedItem.variant_id;

      // Per-variant debounce: rapid clicks on same product → single consolidation
      if (pendingVariants[variantId]) {
        clearTimeout(pendingVariants[variantId]);
      }
      pendingVariants[variantId] = setTimeout(function() {
        delete pendingVariants[variantId];
        consolidateVariant(variantId);
      }, 500);
    });
  }

  /**
   * Consolidate cart items for a given fabric variant.
   * - Single item: update Yardage if stale or missing
   * - Multiple items: zero duplicates, merge into keeper with correct Yardage
   * @param {number} variantId - The variant ID to consolidate
   */
  function consolidateVariant(variantId) {
    fetch('/cart.js')
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        // Match by variant_id alone (finds items with AND without Yardage)
        var matchingItems = [];
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].variant_id === variantId) {
            matchingItems.push(cart.items[i]);
          }
        }

        if (matchingItems.length === 0) return;

        var totalQty = 0;
        for (var j = 0; j < matchingItems.length; j++) {
          totalQty += matchingItems[j].quantity;
        }
        var correctYardage = calculateYardage(totalQty, 0.25);

        if (matchingItems.length === 1) {
          // Single item: update Yardage if stale or missing
          var item = matchingItems[0];
          var currentYardage = (item.properties && item.properties['Yardage']) || '';
          if (currentYardage === correctYardage) return;

          // Preserve any existing properties, update Yardage
          var updatedProps = {};
          var existingProps = item.properties || {};
          for (var prop in existingProps) {
            if (existingProps.hasOwnProperty(prop)) {
              updatedProps[prop] = existingProps[prop];
            }
          }
          updatedProps['Yardage'] = correctYardage;

          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: totalQty,
              properties: updatedProps
            })
          })
            .then(function(res) { return res.json(); })
            .then(function() {
              document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
            });
        }

        // Multiple items: zero duplicates, update keeper
        var keepItem = matchingItems[0];

        // Preserve keeper's existing properties, update Yardage
        var keepProps = {};
        var keepExisting = keepItem.properties || {};
        for (var prop in keepExisting) {
          if (keepExisting.hasOwnProperty(prop)) {
            keepProps[prop] = keepExisting[prop];
          }
        }
        keepProps['Yardage'] = correctYardage;

        var zeroPromises = [];
        for (var k = 1; k < matchingItems.length; k++) {
          zeroPromises.push(
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: matchingItems[k].key, quantity: 0 })
            }).then(function(res) { return res.json(); })
          );
        }

        return Promise.all(zeroPromises).then(function() {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: keepItem.key,
              quantity: totalQty,
              properties: keepProps
            })
          });
        })
          .then(function(res) { return res.json(); })
          .then(function() {
            document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          });
      });
  }

  /**
   * Initialize yardage property injection for PDP forms
   * Listens for form submission and quantity changes
   */
  function initYardagePropertyInjection() {
    // Find fabric product forms
    const fabricSelects = document.querySelectorAll('[data-fabric-select]');

    fabricSelects.forEach(function(popoutSelect) {
      // Listen for popout option selection
      popoutSelect.addEventListener('click', function(event) {
        const option = event.target.closest('[data-popout-option]');
        if (option) {
          const quantity = parseInt(option.getAttribute('data-value'), 10);
          const form = popoutSelect.closest('form');

          if (form && !isNaN(quantity)) {
            addYardageProperty(form, quantity);
          }
        }
      });
    });

    // Also handle form submission directly
    document.querySelectorAll('form[data-product-form]').forEach(function(form) {
      form.addEventListener('submit', function(event) {
        const fabricSelect = form.querySelector('[data-fabric-select]');
        if (fabricSelect) {
          const quantityInput = form.querySelector('input[name="quantity"]');
          if (quantityInput) {
            const quantity = parseInt(quantityInput.value, 10);
            if (!isNaN(quantity)) {
              addYardageProperty(form, quantity);
            }
          }
        }
      });
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initFabricCart();
      initYardagePropertyInjection();
    });
  } else {
    initFabricCart();
    initYardagePropertyInjection();
  }

  // Expose for external use if needed
  window.YLIFabricCart = {
    calculateYardage: calculateYardage,
    addYardageProperty: addYardageProperty
  };

})();
